// src/components/LoadHistoryDetails.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  MapPin,
  Package,
  Route,
  Truck,
  User,
  Weight,
  X,
  XCircle,
  Phone,
  Link as LinkIcon,
  Euro,
  Maximize2,
  Map as MapIcon,
  LocateFixed,
  Pencil,
  AlertTriangle,
  MessageSquareText,
  ChevronUp,
} from "lucide-react";
import "../styles/LoadHistoryDetails.css";

/**
 * LoadHistoryDetails
 *
 * Props:
 * - history?: objeto já carregado
 * - historyId?: number
 * - onClose?: () => void
 * - onReverted?: (restoredLoad) => void
 */
export default function LoadHistoryDetails({ history, historyId, onClose, onReverted }) {
  const { t } = useTranslation("load_history_details");

  const [data, setData] = useState(history || null);
  const [loading, setLoading] = useState(!history);
  const [error, setError] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [whByPlaceId, setWhByPlaceId] = useState({});

  const [openLocationDetails, setOpenLocationDetails] = useState({});

  const isLocationDetailsOpen = (pointKey) => {
    return openLocationDetails[pointKey] === true;
  };

  const toggleLocationDetails = (pointKey) => {
    setOpenLocationDetails((prev) => ({
      ...prev,
      [pointKey]: !prev[pointKey],
    }));
  };

  const onlyPlate = (s) => String(s ?? "").split("•")[0].trim();

  const isAggregateDriverLabel = (value) => {
    const s = String(value ?? "").trim().toLowerCase();

    return (
      /^\d+\s*drivers?$/.test(s) ||
      /^\d+\s*motoristas?$/.test(s) ||
      /^\d+\s*chauffeurs?$/.test(s) ||
      /^\d+\s*conductores?$/.test(s)
    );
  };

  const cleanDriverName = (value) => {
    const s = String(value ?? "").trim();

    if (!s || s === "—") return "";
    if (isAggregateDriverLabel(s)) return "";

    return s;
  };

  const getDriverDisplayName = (...values) => {
    for (const value of values) {
      const cleaned = cleanDriverName(value);
      if (cleaned) return cleaned;
    }

    return "—";
  };

  const getDriverDisplayNames = (...values) => {
    const names = [];

    values.flat().forEach((value) => {
      const cleaned = cleanDriverName(value);
      if (!cleaned) return;
      if (names.includes(cleaned)) return;
      names.push(cleaned);
    });

    return names;
  };

  const formatDriverNames = (...values) => {
    const names = getDriverDisplayNames(...values);
    return names.length ? names.join(" + ") : "—";
  };

  const toUrl = (x) => {
    if (!x) return "";
    if (/^(https?:)?\/\//i.test(x) || x.startsWith("data:") || x.startsWith("blob:")) return x;

    const p = String(x).replace(/\\/g, "/");
    if (p.startsWith("/")) return `${window.location.origin}${p}`;
    if (p.startsWith("uploads/")) return `${window.location.origin}/${p}`;
    return `${window.location.origin}/uploads/${p.replace(/^\/+/, "")}`;
  };

  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  function weekdayKeyFromISO(isoDateStr) {
    if (!isoDateStr) return null;
    const d = new Date(isoDateStr);
    if (Number.isNaN(d)) return null;
    return DAYS[d.getDay()];
  }

  function parseWorkingHours(wh) {
    if (!wh) return null;
    if (typeof wh === "string") {
      try {
        return JSON.parse(wh);
      } catch {
        return null;
      }
    }
    if (typeof wh === "object") return wh;
    return null;
  }

  function formatIntervalsForDay(workingHours, dayKey) {
    if (!workingHours || !dayKey) return null;
    const day = workingHours[dayKey];
    if (!Array.isArray(day)) return null;
    if (day.length === 0) return "__CLOSED__";
    return day.map(([start, end]) => `${start}–${end}`).join(", ");
  }

  useEffect(() => {
    let ignore = false;

    async function fetchDetail() {
      if (!historyId) return;
      if (history && history.clientType != null) return;

      setLoading(true);
      try {
        const res = await axios.get(`/load-history/summary/${historyId}`);
        const payload = res?.data?.history ?? res?.data;
        if (!ignore) setData(payload);
      } catch (err) {
        console.error("❌ Failed to fetch load history detail:", err);
        if (!ignore) setError(t("errors.fetchFailed"));
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    fetchDetail();

    return () => {
      ignore = true;
    };
  }, [historyId, history, t]);

  const load = data?.load || null;
  const snapshot = data?.pickupDeliveriesSnapshot || null;

  const points = useMemo(() => {
    const hasLists =
      (Array.isArray(data?.pickupList) && data.pickupList.length > 0) ||
      (Array.isArray(data?.deliveryList) && data.deliveryList.length > 0);

    if (hasLists) {
      const pick = (data.pickupList || []).map((it, i) => ({
        id: it.id ?? `pl-${i}`,
        type: "pickup",
        sequence: it.sequence ?? i + 1,
        location: it.location ?? "",
        address: it.address ?? "",
        postalCode: it.postalCode ?? "",
        city: it.city ?? "",
        country: it.country ?? "",
        date: it.date ?? null,
        hour: it.hour ?? null,
        status: it.status ?? null,
        gMapsLink: it.gMapsLink ?? null,
        coordinates: it.coordinates ?? null,
        contactPhone: it.contactPhone ?? null,

        // 🆕 point-level dimensions
        length: it.length ?? null,
        width: it.width ?? null,
        height: it.height ?? null,

        loadingAccess: it.loadingAccess ?? null,
        handlingEquipment: it.handlingEquipment ?? null,
        handlingEquipmentOther: it.handlingEquipmentOther ?? null,
        placeId: it.placeId ?? null,
        workingHours: it.workingHours ?? null,
        truckPlate: it.truckPlate ? onlyPlate(it.truckPlate) : null,
        trailerPlate: it.trailerPlate ?? null,
        driverName: getDriverDisplayName(
          it.driverName,
          it.driverFullName,
          it.driverInfo,
          it.driver?.fullname,
          it.driver?.fullName,
          it.driver?.name
        ),
        driver2Name: getDriverDisplayName(
          it.driver2Name,
          it.secondDriver?.fullname,
          it.secondDriver?.fullName,
          it.secondDriver?.name
        ),
        driverNames: Array.isArray(it.driverNames)
          ? it.driverNames
          : [
              it.driverName,
              it.driver2Name,
              it.driverFullName,
              it.driverInfo,
              it.driver?.fullname,
              it.driver?.fullName,
              it.driver?.name,
              it.secondDriver?.fullname,
              it.secondDriver?.fullName,
              it.secondDriver?.name,
            ].filter(Boolean),

        driverCompletionHasIssue:
          !!it.driverCompletionHasIssue,

        driverCompletionNote:
          it.driverCompletionNote ?? null,

        driverCompletionNoteAt:
          it.driverCompletionNoteAt ?? null,

        driverCompletionNoteDriverId:
          it.driverCompletionNoteDriverId ?? null,

        driverCompletionNoteDriverName:
          it.driverCompletionNoteDriverName ?? null,

        latestStatusEvent: it.latestStatusEvent ?? null,
        truckId: null,
        truck: null,
        trailerId: null,
        trailer: null,
        driverId: null,
        driver: null,
      }));

      const del = (data.deliveryList || []).map((it, i) => ({
        id: it.id ?? `dl-${i}`,
        type: "delivery",
        sequence: it.sequence ?? i + 1,
        location: it.location ?? "",
        address: it.address ?? "",
        postalCode: it.postalCode ?? "",
        city: it.city ?? "",
        country: it.country ?? "",
        date: it.date ?? null,
        hour: it.hour ?? null,
        status: it.status ?? null,
        gMapsLink: it.gMapsLink ?? null,
        coordinates: it.coordinates ?? null,
        contactPhone: it.contactPhone ?? null,

        // 🆕 point-level dimensions
        length: it.length ?? null,
        width: it.width ?? null,
        height: it.height ?? null,

        loadingAccess: it.loadingAccess ?? null,
        handlingEquipment: it.handlingEquipment ?? null,
        handlingEquipmentOther: it.handlingEquipmentOther ?? null,
        placeId: it.placeId ?? null,
        workingHours: it.workingHours ?? null,
        truckPlate: it.truckPlate ? onlyPlate(it.truckPlate) : null,
        trailerPlate: it.trailerPlate ?? null,
        driverName: getDriverDisplayName(
          it.driverName,
          it.driverFullName,
          it.driverInfo,
          it.driver?.fullname,
          it.driver?.fullName,
          it.driver?.name
        ),
        driver2Name: getDriverDisplayName(
          it.driver2Name,
          it.secondDriver?.fullname,
          it.secondDriver?.fullName,
          it.secondDriver?.name
        ),
        driverNames: Array.isArray(it.driverNames)
          ? it.driverNames
          : [
              it.driverName,
              it.driver2Name,
              it.driverFullName,
              it.driverInfo,
              it.driver?.fullname,
              it.driver?.fullName,
              it.driver?.name,
              it.secondDriver?.fullname,
              it.secondDriver?.fullName,
              it.secondDriver?.name,
            ].filter(Boolean),

        driverCompletionHasIssue:
          !!it.driverCompletionHasIssue,

        driverCompletionNote:
          it.driverCompletionNote ?? null,

        driverCompletionNoteAt:
          it.driverCompletionNoteAt ?? null,

        driverCompletionNoteDriverId:
          it.driverCompletionNoteDriverId ?? null,

        driverCompletionNoteDriverName:
          it.driverCompletionNoteDriverName ?? null,

        latestStatusEvent: it.latestStatusEvent ?? null,
        truckId: null,
        truck: null,
        trailerId: null,
        trailer: null,
        driverId: null,
        driver: null,
      }));

      return [...pick, ...del].sort(sortPoints);
    }

    const arr = Array.isArray(snapshot)
      ? snapshot
      : Array.isArray(load?.pickupDeliveries)
      ? load.pickupDeliveries
      : [];

    return arr
      .map((p, i) => ({
        id: p.id ?? `pd-${i}`,
        type: p.type?.toLowerCase?.() || p.type || "pickup",
        sequence: p.sequence ?? i + 1,
        location: p.location ?? "",
        address: p.address ?? "",
        postalCode: p.postalCode ?? "",
        city: p.city ?? "",
        country: p.country ?? "",
        date: p.date ?? null,
        hour: p.hour ?? null,
        status: p.status ?? null,
        gMapsLink: p.gMapsLink ?? null,
        coordinates: p.coordinates ?? null,
        contactPhone: p.contactPhone ?? null,

        // 🆕 point-level dimensions
        length: p.length ?? null,
        width: p.width ?? null,
        height: p.height ?? null,

        loadingAccess: p.loadingAccess ?? null,
        handlingEquipment: p.handlingEquipment ?? null,
        handlingEquipmentOther: p.handlingEquipmentOther ?? null,
        placeId: p.placeId ?? p.place?.id ?? null,
        workingHours: p.workingHours ?? p.place?.workingHours ?? p.placeWorkingHours ?? null,
        truckId: p.truckId ?? p.truckIdArrival ?? p.truckIdDeparture ?? null,
        truck: p.truck || p.truckArrival || p.truckDeparture || null,
        trailerId: p.trailerId ?? null,
        trailer: p.trailer ?? null,
        driverId: p.driverId ?? null,
        driver: p.driver ?? null,
        truckPlate: p.truck?.plate ? onlyPlate(p.truck.plate) : p.truckPlate ? onlyPlate(p.truckPlate) : null,
        trailerPlate: p.trailer?.trailerNumber ?? p.trailerPlate ?? null,
        driverName: getDriverDisplayName(
          p.driver?.fullname,
          p.driver?.fullName,
          p.driver?.name,
          p.driver?.driverName,
          p.driverName,
          p.driverFullName,
          p.driverInfo
        ),
        driver2Name: getDriverDisplayName(
          p.secondDriver?.fullname,
          p.secondDriver?.fullName,
          p.secondDriver?.name,
          p.driver2Name
        ),
        driverNames: Array.isArray(p.driverNames)
          ? p.driverNames
          : [
              p.driver?.fullname,
              p.driver?.fullName,
              p.driver?.name,
              p.driver?.driverName,
              p.driverName,
              p.driverFullName,
              p.driverInfo,
              p.secondDriver?.fullname,
              p.secondDriver?.fullName,
              p.secondDriver?.name,
              p.driver2Name,
            ].filter(Boolean),

        driverCompletionHasIssue:
          !!p.driverCompletionHasIssue,

        driverCompletionNote:
          p.driverCompletionNote ?? null,

        driverCompletionNoteAt:
          p.driverCompletionNoteAt ?? null,

        driverCompletionNoteDriverId:
          p.driverCompletionNoteDriverId ?? null,

        driverCompletionNoteDriverName:
          p.driverCompletionNoteDriverName ?? null,

        latestStatusEvent: p.latestStatusEvent ?? null,
      }))
      .sort(sortPoints);
  }, [data?.pickupList, data?.deliveryList, snapshot, load]);

  function sortPoints(a, b) {
    const ad = a.date || "";
    const bd = b.date || "";
    const ah = a.hour || "";
    const bh = b.hour || "";

    if (ad !== bd) return ad.localeCompare(bd);
    if (ah !== bh) return ah.localeCompare(bh);
    if (a.type !== b.type) return a.type === "pickup" ? -1 : 1;

    return (a.sequence ?? 0) - (b.sequence ?? 0);
  }

  useEffect(() => {
    const need = Array.from(
      new Set(
        points
          .filter(
            (p) =>
              p.placeId &&
              !p.workingHours &&
              !Object.prototype.hasOwnProperty.call(whByPlaceId, p.placeId)
          )
          .map((p) => p.placeId)
      )
    );

    if (need.length === 0) return;

    let ignore = false;

    (async () => {
      try {
        const results = await Promise.all(
          need.map((id) =>
            axios
              .get(`/places/${id}`, { params: { fields: "workingHours" } })
              .then((res) => ({ id, wh: res.data?.workingHours ?? null }))
              .catch(() => ({ id, wh: null }))
          )
        );

        if (ignore) return;

        setWhByPlaceId((prev) => {
          let changed = false;
          const next = { ...prev };

          for (const { id, wh } of results) {
            if (!Object.prototype.hasOwnProperty.call(next, id) || next[id] !== wh) {
              next[id] = wh;
              changed = true;
            }
          }

          return changed ? next : prev;
        });
      } catch {
        // silent fallback
      }
    })();

    return () => {
      ignore = true;
    };
  }, [points, whByPlaceId]);

  async function downloadCMR(src) {
    if (!src) return;

    const url = toUrl(src);
    const filename = String(src).split("/").pop() || "cmr.jpg";

    try {
      const res = await axios.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");

      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("⚠️ CMR blob download failed:", err);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

const getPointTypeLabel = (type) => {
  const normalized = String(type || "").toLowerCase();

  if (normalized === "pickup") {
    return t("point.pickup", { defaultValue: "Recolha" });
  }

  if (normalized === "delivery") {
    return t("point.delivery", { defaultValue: "Entrega" });
  }

  return t("docs.unknownPoint", { defaultValue: "Ponto" });
};

const getPointForDocument = (doc) => {
  const docPointId =
    doc?.pickupDeliveryId ??
    doc?.pointId ??
    doc?.pickupdeliveryId ??
    doc?.pickup_delivery_id ??
    null;

  if (docPointId != null) {
    const byId = points.find((p) => String(p.id) === String(docPointId));
    if (byId) return byId;
  }

  const docPointType = String(doc?.pointType || doc?.type || "")
    .trim()
    .toLowerCase();

  const docPointSequence =
    doc?.pointSequence ??
    doc?.sequence ??
    doc?.point_sequence ??
    null;

  if (docPointType && docPointSequence != null) {
    const byTypeAndSequence = points.find(
      (p) =>
        String(p.type).toLowerCase() === docPointType &&
        String(p.sequence) === String(docPointSequence)
    );

    if (byTypeAndSequence) return byTypeAndSequence;
  }

  return null;
};

const getDocumentSrc = (item) => {
  if (!item) return "";

  if (typeof item === "string") return item;

  if (typeof item === "object" && !Array.isArray(item)) {
    return (
      item.url ||
      item.fileUrl ||
      item.src ||
      item.path ||
      item.key ||
      item.location ||
      ""
    );
  }

  return "";
};

const cmrPhotos = useMemo(() => {
  const arr = Array.isArray(data?.loadDocPhotos)
    ? data.loadDocPhotos
    : Array.isArray(load?.loadDocPhotos)
    ? load.loadDocPhotos
    : [];

  if (!Array.isArray(arr)) return [];

  return arr
    .map((item, index) => {
      const isObject = item && typeof item === "object" && !Array.isArray(item);
      const src = getDocumentSrc(item);

      if (!src) return null;

      const point = isObject ? getPointForDocument(item) : null;

      return {
        id: isObject ? item.id ?? `doc-${index}` : `legacy-doc-${index}`,
        src,
        point,
        hasPointMetadata: Boolean(point),
        raw: item,
      };
    })
    .filter(Boolean);
}, [data, load, points]);

const groupedCmrDocs = useMemo(() => {
  const groupsMap = new Map();

  cmrPhotos.forEach((doc, index) => {
    const point = doc.point;

    const groupKey = point
      ? `${point.type}-${point.id ?? point.sequence}`
      : "unknown";

    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, {
        key: groupKey,
        point,
        docs: [],
      });
    }

    groupsMap.get(groupKey).docs.push({
      ...doc,
      displayIndex: index + 1,
    });
  });

  const groups = Array.from(groupsMap.values());

  return groups.sort((a, b) => {
    if (!a.point && !b.point) return 0;
    if (!a.point) return 1;
    if (!b.point) return -1;

    return (a.point.sequence ?? 0) - (b.point.sequence ?? 0);
  });
}, [cmrPhotos]);

  const clientLabel = useMemo(() => {
    if (data?.client) return data.client;

    const type = load?.clientType || null;
    const industryName = load?.industryClient?.companyName || load?.industryClientName || null;
    const carrierClientName = load?.carrierClient?.companyName || load?.carrierClientName || null;

    if (type === "INDUSTRY" && industryName) return industryName;

    if (type === "CARRIER" && carrierClientName) {
      return t("clientLabel.carrierClient", {
        name: carrierClientName,
        defaultValue: `${carrierClientName} (client)`,
      });
    }

    return industryName || carrierClientName || "—";
  }, [data?.client, load, t]);

  const typeRaw = (data?.clientType || load?.clientType || "").toUpperCase();

  const typeNice =
    typeRaw === "INDUSTRY"
      ? t("types.industry")
      : typeRaw === "CARRIER"
      ? t("types.carrier")
      : t("types.unknown");

  const executorCarrier = useMemo(() => {
    return (
      data?.carrierCompanyName ||
      load?.carrier?.companyName ||
      load?.carrierName ||
      load?.carrierInfo ||
      "—"
    );
  }, [data?.carrierCompanyName, load]);

  const trailerTypeDisplay = useMemo(() => {
    const raw = data?.trailerType ?? load?.trailerType ?? null;
    const other = data?.trailerTypeOther ?? load?.trailerTypeOther ?? null;

    const v = String(raw ?? "").trim().toUpperCase();
    const o = String(other ?? "").trim();

    if (!v) return null;

    if (v === "OTHER") {
      return o || t("trailerType.options.other", { defaultValue: "Other" });
    }

    return t(`trailerType.options.${v.toLowerCase()}`, { defaultValue: v });
  }, [data?.trailerType, data?.trailerTypeOther, load?.trailerType, load?.trailerTypeOther, t]);

  const fmtDim = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

  const fmtPointDim = (v) => {
    if (v === null || v === undefined || v === "") return "—";

    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);

    return n.toLocaleString("pt-PT", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const hasPointDimensions = (point) => {
    return point?.length != null || point?.width != null || point?.height != null;
  };

  const formatPointDimensions = (point) => {
    if (!hasPointDimensions(point)) return "—";

    return `${fmtPointDim(point.length)} × ${fmtPointDim(point.width)} × ${fmtPointDim(point.height)} m`;
  };

  const fmtWeightKg = (v) => {
    if (v === null || v === undefined || v === "") return "—";

    const kg = Number(v);
    if (Number.isNaN(kg)) return `${String(v)} kg`;

    const formatted = kg.toLocaleString("pt-PT", {
      minimumFractionDigits: 0,
      maximumFractionDigits: Number.isInteger(kg) ? 0 : 2,
    });

    return `${formatted} kg`;
  };

  const fmtMoney = (v) => {
    if (v == null || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);

    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const dimL = useMemo(() => data?.merchLength ?? load?.length ?? null, [data?.merchLength, load?.length]);
  const dimW = useMemo(() => data?.merchWidth ?? load?.width ?? null, [data?.merchWidth, load?.width]);
  const dimH = useMemo(() => data?.merchHeight ?? load?.height ?? null, [data?.merchHeight, load?.height]);
  const dimWeight = useMemo(() => data?.weight ?? load?.weight ?? null, [data?.weight, load?.weight]);

  const merchandiseValue = useMemo(() => {
    const m = data?.merchandise ?? load?.merchandise ?? null;
    if (m == null) return null;

    const s = String(m).trim();
    return s.length ? s : null;
  }, [data?.merchandise, load?.merchandise]);

  const priceValue = useMemo(
    () => (data?.price != null && data.price !== "" ? data.price : load?.price ?? null),
    [data?.price, load?.price]
  );

  const notesText = useMemo(() => {
    const n = data?.notes ?? load?.notes ?? null;
    if (typeof n !== "string") return null;

    const txt = n.trim();
    return txt.length ? txt : null;
  }, [data?.notes, load?.notes]);

  const notes4driverText = useMemo(() => {
    const n = data?.notes4driver ?? load?.notes4driver ?? null;
    if (typeof n !== "string") return null;

    const txt = n.trim();
    return txt.length ? txt : null;
  }, [data?.notes4driver, load?.notes4driver]);

  const firstDate = useMemo(() => {
    for (const p of points) {
      if (p.date) return p.date;
    }
    return null;
  }, [points]);

  const sessionSummary = useMemo(() => {
    const pickups = points.filter((p) => p.type === "pickup");
    const deliveries = points.filter((p) => p.type === "delivery");

    const completedPickup = pickups.some((p) => String(p.status || "").toUpperCase() === "COMPLETED");
    const cancelledPickup = pickups.some((p) => String(p.status || "").toUpperCase() === "CANCELLED");

    const completedDelivery = deliveries.some((p) => String(p.status || "").toUpperCase() === "COMPLETED");
    const cancelledDelivery = deliveries.some((p) => String(p.status || "").toUpperCase() === "CANCELLED");

    return {
      completedPickup,
      cancelledPickup,
      completedDelivery,
      cancelledDelivery,
      pickupsCount: pickups.length,
      deliveriesCount: deliveries.length,
    };
  }, [points]);

  function formatDate(dateStr) {
    if (!dateStr) return "—";

    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  }

  function formatHour(h) {
    if (!h) return "—";
    if (/^\d{4}$/.test(h)) return `${h.slice(0, 2)}:${h.slice(2)}`;
    return h;
  }

  function formatStatusEventHour(event) {
    const raw = event?.phoneTimestamp || event?.eventTimestamp || event?.createdAt || null;

    if (!raw) return null;

    try {
      return new Date(raw).toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  }

  function formatDriverCompletionDateTime(value) {
    if (!value) return null;

    try {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date.toLocaleString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  }

  function formatGpsDistance(meters) {
    const n = Number(meters);

    if (!Number.isFinite(n) || n < 0) return null;

    if (n < 1000) {
      return t("statusGps.distanceMeters", {
        distance: Math.round(n).toLocaleString(),
        defaultValue: `${Math.round(n).toLocaleString()} m de distância`,
      });
    }

    const km = n / 1000;

    const formattedKm = km.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    return t("statusGps.distanceKm", {
      distance: formattedKm,
      defaultValue: `${formattedKm} km de distância`,
    });
  }

  function joinGpsDetails(...parts) {
    return parts
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" · ");
  }

  function getHistoryGpsMeta(point) {
    const event = point?.latestStatusEvent || null;

    if (!event) {
      return {
        type: "missing",
        Icon: AlertTriangle,
        title: t("statusGps.noEvent", {
          defaultValue: "Sem confirmação GPS",
        }),
        detail: null,
      };
    }

    const time = formatStatusEventHour(event);
    const distanceText = formatGpsDistance(event.distanceFromPlaceMeters);

    const hasGps =
      event.latitude != null &&
      event.longitude != null &&
      String(event.gpsSource || "").toUpperCase() !== "UNAVAILABLE";

    if (!hasGps) {
      return {
        type: "missing",
        Icon: AlertTriangle,
        title: t("statusGps.noGps", {
          defaultValue: "Sem GPS",
        }),
        detail: time,
      };
    }

    if (event.locationMatch === true) {
      return {
        type: "ok",
        Icon: LocateFixed,
        title: t("statusGps.onLocation", {
          defaultValue: "GPS confirmado no local",
        }),
        detail: time,
      };
    }

    if (event.locationMatch === false) {
      return {
        type: "warning",
        Icon: AlertTriangle,
        title: t("statusGps.outsideLocation", {
          defaultValue: "GPS fora do local",
        }),
        detail: joinGpsDetails(distanceText, time),
      };
    }

    return {
      type: "neutral",
      Icon: LocateFixed,
      title: t("statusGps.gpsReceived", {
        defaultValue: "GPS recebido",
      }),
      detail: joinGpsDetails(distanceText, time),
    };
  }

  function renderHistoryGpsProof(point) {
    const meta = getHistoryGpsMeta(point);
    const Icon = meta.Icon;

    return (
      <div className={`load-history-details-gps-proof load-history-details-gps-proof-${meta.type}`}>
        <div className="load-history-details-gps-proof-main">
          <Icon className="load-history-details-gps-proof-icon" />
          <span>{meta.title}</span>
          {meta.detail && <span className="load-history-details-gps-proof-separator">·</span>}
          {meta.detail && <span className="load-history-details-gps-proof-inline-detail">{meta.detail}</span>}
        </div>
      </div>
    );
  }

  function formatCoords(coords) {
    if (!coords) return "—";
    if (typeof coords === "string") return coords;

    if (Array.isArray(coords) && coords.length >= 2) {
      const [lat, lng] = coords;
      return `${lat}, ${lng}`;
    }

    if (coords?.lat != null && coords?.lng != null) return `${coords.lat}, ${coords.lng}`;

    return String(coords);
  }

  const statusKey = (status) => {
    const s = String(status || "").toUpperCase();

    if (s === "COMPLETED") return "completed";
    if (s === "CANCELLED") return "cancelled";
    if (s === "IN_PROGRESS") return "inProgress";
    if (s === "PENDING") return "pending";

    return "unknown";
  };

  const renderStatusBadge = (status) => {
    const sKey = statusKey(status);
    const sRaw = String(status || "").toUpperCase();

    const Icon = sRaw === "COMPLETED" ? CheckCircle2 : sRaw === "CANCELLED" ? XCircle : Clock;

    const cls =
      sRaw === "COMPLETED"
        ? "load-history-details-badge load-history-details-badge-success"
        : sRaw === "CANCELLED"
        ? "load-history-details-badge load-history-details-badge-danger"
        : sRaw === "IN_PROGRESS"
        ? "load-history-details-badge load-history-details-badge-info"
        : sRaw === "PENDING"
        ? "load-history-details-badge load-history-details-badge-warn"
        : "load-history-details-badge";

    return (
      <span className={cls}>
        <Icon className="load-history-details-badge-icon" />
        {t(`status.${sKey}`)}
      </span>
    );
  };

  const renderSummaryPill = (type, status) => {
    const isPickup = type === "pickup";
    const isCompleted = status === "completed";

    const Icon = isCompleted ? CheckCircle2 : XCircle;

    return (
      <span
        className={`load-history-details-summary-pill ${
          isCompleted
            ? "load-history-details-summary-pill-success"
            : "load-history-details-summary-pill-danger"
        }`}
      >
        <Icon className="load-history-details-summary-pill-icon" />
        {isPickup
          ? isCompleted
            ? t("summary.pickupCompleted", { defaultValue: "Recolha Concluída" })
            : t("summary.pickupCancelled", { defaultValue: "Recolha Cancelada" })
          : isCompleted
          ? t("summary.deliveryCompleted", { defaultValue: "Entrega Concluída" })
          : t("summary.deliveryCancelled", { defaultValue: "Entrega Cancelada" })}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="load-history-details-overlay">
        <div className="load-history-details-loading">
          <Loader2 className="load-history-details-spin" />
          <div>{t("loading.title")}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="load-history-details-overlay">
        <div className="load-history-details-error">
          <div className="load-history-details-error-head">
            <XCircle className="load-history-details-error-icon" />
            <span>{error}</span>
          </div>

          <button onClick={onClose} className="load-history-details-btn-secondary">
            <ArrowLeft className="load-history-details-btn-icon" />
            {t("buttons.back")}
          </button>
        </div>
      </div>
    );
  }

  const loadIdVal = (load?.loadNumber ?? data?.loadNumber ?? load?.id ?? data?.loadId) ?? "—";
  const tsIdVal = (load?.transportsessionId ?? data?.transportSessionId) ?? "—";
  const tsNumberVal = data?.transportSessionNumber ?? data?.sessionNumber ?? null;

  return (
    <div className="load-history-details-root">
      <div className="load-history-details-backdrop" onClick={onClose} />

      <section className="load-history-details-panel">
        <header className="load-history-details-header">
          <div className="load-history-details-header-row">
            <div className="load-history-details-header-left">
              <button onClick={onClose} className="load-history-details-close">
                <X className="load-history-details-close-icon" />
                {t("buttons.close")}
              </button>

              <div className="load-history-details-title">
                <span>{t("titles.page")}</span>
              </div>
            </div>

            <div className="load-history-details-header-right">
              <span className="load-history-details-chip">
                {t("chips.loadId", {
                  id: loadIdVal,
                  defaultValue: `Carga #${loadIdVal}`,
                })}
              </span>

              {tsNumberVal && (
                <span className="load-history-details-chip">
                  {t("chips.tsNumber", {
                    number: tsNumberVal,
                    defaultValue: `Sessão #${tsNumberVal}`,
                  })}
                </span>
              )}

              <span className="load-history-details-chip">
                {t("chips.transportSessionId", {
                  id: tsIdVal,
                  defaultValue: `Id Sessão de Transporte: ${tsIdVal}`,
                })}
              </span>

              {firstDate && (
                <span className="load-history-details-chip">
                  <CalendarClock className="load-history-details-chip-icon" />
                  {formatDate(firstDate)}
                </span>
              )}
            </div>
          </div>
        </header>

        <main className="load-history-details-content">
          <div className="load-history-details-summary-cards">
            <SummaryCard icon={<User />} label={t("cards.client")} value={clientLabel} />

            <SummaryCard icon={<Truck />} label={t("cards.transportCarrier")} value={executorCarrier} />

            <SummaryCard icon={<Euro />} label={t("cards.price")} value={`€ ${fmtMoney(priceValue)}`} accent="money" />

            <SummaryCard
              icon={<Package />}
              label={t("fields.merchandise", { defaultValue: "Merchandise" })}
              value={merchandiseValue ?? "—"}
            />

            <SummaryCard
              icon={<Maximize2 />}
              label={t("cards.dimensions", { defaultValue: "Dimensões" })}
              value={`${fmtDim(dimL)} x ${fmtDim(dimW)} x ${fmtDim(dimH)} m`}
            />

            <SummaryCard
              icon={<Weight />}
              label={t("dimensions.weight", { defaultValue: "Peso" })}
              value={fmtWeightKg(dimWeight)}
            />

            <SummaryCard
              icon={<Truck />}
              label={t("fields.trailerType", { defaultValue: "Tipo de Reboque" })}
              value={trailerTypeDisplay ?? "—"}
            />
          </div>

          <div className="load-history-details-body">
            <section className="load-history-details-timeline">
              <div className="load-history-details-timeline-head">
                <Route className="load-history-details-timeline-head-icon" />
                <span>{t("titles.timeline")}</span>
              </div>

              <ol className="load-history-details-steps">
                {points.length === 0 && <div className="load-history-details-empty">{t("empty.noPoints")}</div>}

                {points.map((p, idx) => {
                  const isPickup = p.type === "pickup";

                  const tagText = isPickup
                    ? t("point.pickup")
                    : p.type === "delivery"
                    ? t("point.delivery")
                    : String(p.type || "").toUpperCase();

                  const pointStatus = String(p.status || "").toUpperCase();

                  const pointDotClass =
                    pointStatus === "COMPLETED"
                      ? "load-history-details-step-dot-completed"
                      : pointStatus === "CANCELLED"
                      ? "load-history-details-step-dot-cancelled"
                      : "load-history-details-step-dot-neutral";

                  const pointStepClass =
                    pointStatus === "COMPLETED"
                      ? "load-history-details-step-completed"
                      : pointStatus === "CANCELLED"
                      ? "load-history-details-step-cancelled"
                      : "load-history-details-step-neutral";

                  const driverCompletionNote = String(
                    p.driverCompletionNote || ""
                  ).trim();

                  const hasDriverCompletionNote =
                    p.driverCompletionHasIssue === true &&
                    driverCompletionNote.length > 0;

                  const driverCompletionDateTime =
                    formatDriverCompletionDateTime(
                      p.driverCompletionNoteAt
                    );

                  return (
                    <li key={`${p.type}-${p.id ?? idx}`} className={`load-history-details-step ${pointStepClass}`}>
                      <div className={`load-history-details-step-dot ${pointDotClass}`}>
                        {pointStatus === "COMPLETED" ? (
                          <Check className="load-history-details-step-dot-icon" />
                        ) : pointStatus === "CANCELLED" ? (
                          <X className="load-history-details-step-dot-icon" />
                        ) : isPickup ? (
                          <MapPin className="load-history-details-step-dot-icon" />
                        ) : (
                          <Package className="load-history-details-step-dot-icon" />
                        )}
                      </div>

                      <div className="load-history-details-step-card">
                        <div className="load-history-details-step-mainline">
                          <div className="load-history-details-step-titlezone">
                            <div className="load-history-details-step-tags">
                              <span
                                className={`load-history-details-tag ${
                                  pointStatus === "COMPLETED"
                                    ? "load-history-details-tag-completed"
                                    : pointStatus === "CANCELLED"
                                    ? "load-history-details-tag-cancelled"
                                    : isPickup
                                    ? "load-history-details-tag-pickup"
                                    : "load-history-details-tag-delivery"
                                }`}
                              >
                                {tagText} #{p.sequence ?? idx + 1}
                              </span>

                              {p.status && renderStatusBadge(p.status)}
                            </div>

                            <div className="load-history-details-step-title">
                              <MapPin className="load-history-details-step-title-icon" />
                              {p.location || `${p.city} ${p.country}` || "—"}
                            </div>

                            <div className="load-history-details-step-meta">
                              <span className="load-history-details-step-meta-item">
                                <CalendarClock className="load-history-details-step-meta-icon" />
                                {formatDate(p.date)}
                                {formatHour(p.hour) !== "—" ? ` · ${formatHour(p.hour)}` : ""}
                              </span>

                              {(p.city || p.country) && (
                                <span className="load-history-details-step-meta-item">
                                  <MapPin className="load-history-details-step-meta-icon" />
                                  {p.city || ""}
                                  {p.city && p.country ? ", " : ""}
                                  {p.country || ""}
                                </span>
                              )}

                              {p.contactPhone && (
                                <span className="load-history-details-step-meta-item">
                                  <Phone className="load-history-details-step-meta-icon" />
                                  {p.contactPhone}
                                </span>
                              )}

                              {hasPointDimensions(p) && (
                                <span className="load-history-details-step-meta-item">
                                  <Maximize2 className="load-history-details-step-meta-icon" />
                                  {formatPointDimensions(p)}
                                </span>
                              )}
                            </div>

                            {renderHistoryGpsProof(p)}

                            {hasDriverCompletionNote && (
                              <div className="load-history-details-driver-note">
                                <div className="load-history-details-driver-note-head">
                                  <MessageSquareText
                                    className="load-history-details-driver-note-icon"
                                    aria-hidden="true"
                                  />

                                  <span className="load-history-details-driver-note-title">
                                    {t("driverOccurrence.title", {
                                      defaultValue:
                                        "Ocorrência comunicada pelo motorista",
                                    })}
                                  </span>
                                </div>

                                <div className="load-history-details-driver-note-text">
                                  {driverCompletionNote}
                                </div>

                                {(p.driverCompletionNoteDriverName ||
                                  driverCompletionDateTime) && (
                                  <div className="load-history-details-driver-note-meta">
                                    {p.driverCompletionNoteDriverName && (
                                      <span>
                                        {p.driverCompletionNoteDriverName}
                                      </span>
                                    )}

                                    {p.driverCompletionNoteDriverName &&
                                      driverCompletionDateTime && (
                                        <span
                                          className="load-history-details-driver-note-separator"
                                          aria-hidden="true"
                                        >
                                          ·
                                        </span>
                                      )}

                                    {driverCompletionDateTime && (
                                      <span>
                                        {driverCompletionDateTime}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="load-history-details-step-assets">
                            <AssetPill
                              type="truck"
                              icon={<Truck />}
                              label={t("fields.truck")}
                              value={p.truckPlate || p.truck?.plate || p.truck?.truckPlate || p.truck?.name || p.truckId || "—"}
                            />

                            <AssetPill
                              type="trailer"
                              icon={<Package />}
                              label={t("fields.trailer")}
                              value={p.trailerPlate || p.trailer?.trailerPlate || p.trailer?.plate || p.trailerId || "—"}
                            />

                            <AssetPill
                              type="driver"
                              icon={<User />}
                              label={t("fields.driver")}
                              value={formatDriverNames(
                                Array.isArray(p.driverNames) ? p.driverNames : [],
                                p.driverName,
                                p.driver2Name,
                                p.driver?.fullname,
                                p.driver?.fullName,
                                p.driver?.name,
                                p.driver?.driverName,
                                p.secondDriver?.fullname,
                                p.secondDriver?.fullName,
                                p.secondDriver?.name
                              )}
                            />
                          </div>
                        </div>

                        <div className="load-history-details-step-fields">
                          {(() => {
                            const locationDetailsKey = `${p.type}-${p.id ?? idx}`;
                            const locationDetailsOpen = isLocationDetailsOpen(locationDetailsKey);

                            return (
                              <div
                                className={`load-history-details-fieldcard ${
                                  !locationDetailsOpen ? "load-history-details-fieldcard-collapsed" : ""
                                }`}
                              >
                                <button
                                  type="button"
                                  className="load-history-details-fieldcard-head"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleLocationDetails(locationDetailsKey);
                                  }}
                                  aria-expanded={locationDetailsOpen}
                                >
                                  <div className="load-history-details-fieldcard-head-left">
                                    <MapIcon className="load-history-details-fieldcard-head-icon" />
                                    <span>{t("titles.locationDetails")}</span>
                                  </div>

                                  <ChevronUp
                                    className={`load-history-details-fieldcard-head-chevron ${
                                      !locationDetailsOpen ? "load-history-details-fieldcard-head-chevron-closed" : ""
                                    }`}
                                  />
                                </button>

                                {(() => {
                                  const dayKey = weekdayKeyFromISO(p.date);
                                  const whRaw = p.workingHours ?? (p.placeId ? whByPlaceId[p.placeId] : null);
                                  const whObj = parseWorkingHours(whRaw);
                                  const intervalsRaw = formatIntervalsForDay(whObj, dayKey);

                                  const dayLabel = dayKey ? t(`days.short.${dayKey}`, { defaultValue: dayKey }) : null;

                                  const intervals =
                                    intervalsRaw === "__CLOSED__"
                                      ? t("workingHours.closed")
                                      : intervalsRaw || t("workingHours.fallback");

                                  const workingHoursValue =
                                    whObj && dayLabel ? `${dayLabel}: ${intervals}` : t("workingHours.fallback");

                                  return (
                                    <div
                                      className={`load-history-details-location-grid-wrap ${
                                        locationDetailsOpen ? "is-expanded" : "is-collapsed"
                                      }`}
                                    >
                                      <div className="load-history-details-location-grid">
                                        <MiniField
                                          label={t("fields.workingHours")}
                                          value={workingHoursValue}
                                          className="load-history-details-loc-hours"
                                        />

                                      <MiniField
                                        label={t("fields.pointDimensions", { defaultValue: "Dimensões da carga neste ponto" })}
                                        value={formatPointDimensions(p)}
                                        className="load-history-details-loc-dimensions"
                                      />

                                      <MiniField
                                        label={t("fields.address")}
                                        value={p.address || "—"}
                                        className="load-history-details-loc-address"
                                      />

                                      <MiniField
                                        label={t("fields.postalCode")}
                                        value={p.postalCode || "—"}
                                        className="load-history-details-loc-postal"
                                      />

                                      <MiniField
                                        label={t("fields.city")}
                                        value={p.city || "—"}
                                        className="load-history-details-loc-city"
                                      />

                                      <MiniField
                                        label={t("fields.country")}
                                        value={p.country || "—"}
                                        className="load-history-details-loc-country"
                                      />

                                      <MiniField
                                        label={t("fields.coordinates")}
                                        value={formatCoords(p.coordinates)}
                                        mono
                                        className="load-history-details-loc-coords"
                                      />

                                      <MiniField
                                        label={t("fields.maps")}
                                        value={
                                          p.gMapsLink ? (
                                            <a
                                              className="load-history-details-link"
                                              href={p.gMapsLink}
                                              target="_blank"
                                              rel="noreferrer"
                                              title={t("workingHours.openMap")}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <LinkIcon className="load-history-details-link-icon" />
                                              {t("workingHours.openMap")}
                                            </a>
                                          ) : (
                                            "—"
                                          )
                                        }
                                        className="load-history-details-loc-map"
                                      />

                                      <MiniField
                                        label={t("fields.loadingAccess", { defaultValue: "Loading access" })}
                                        value={
                                          p.loadingAccess
                                            ? t(`loadingAccess.${String(p.loadingAccess).toLowerCase()}`, {
                                                defaultValue: String(p.loadingAccess),
                                              })
                                            : "—"
                                        }
                                        className="load-history-details-loc-access"
                                      />

                                        <MiniField
                                          label={t("fields.handlingEquipment", {
                                            defaultValue: "Handling equipment",
                                          })}
                                          value={
                                            String(p.handlingEquipment ?? "").toUpperCase() === "OTHER"
                                              ? String(p.handlingEquipmentOther ?? "").trim() || "—"
                                              : p.handlingEquipment
                                              ? t(`handlingEquipment.${String(p.handlingEquipment).toLowerCase()}`, {
                                                  defaultValue: String(p.handlingEquipment),
                                                })
                                              : "—"
                                          }
                                          className="load-history-details-loc-equipment"
                                        />
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>

            <aside className="load-history-details-aside">
              <div className="load-history-details-box">
                <div className="load-history-details-box-head">
                  <div className="load-history-details-box-head-left">
                    <CheckCircle2 className="load-history-details-box-head-icon" />
                    <span>{t("titles.sessionSummary", { defaultValue: "Resumo da Sessão" })}</span>
                  </div>
                </div>

                <div className="load-history-details-session-summary">
                  <div className="load-history-details-summary-pills">
                    {sessionSummary.completedPickup && renderSummaryPill("pickup", "completed")}
                    {sessionSummary.cancelledPickup && renderSummaryPill("pickup", "cancelled")}
                    {sessionSummary.completedDelivery && renderSummaryPill("delivery", "completed")}
                    {sessionSummary.cancelledDelivery && renderSummaryPill("delivery", "cancelled")}

                    {!sessionSummary.completedPickup &&
                      !sessionSummary.cancelledPickup &&
                      !sessionSummary.completedDelivery &&
                      !sessionSummary.cancelledDelivery && (
                        <span className="load-history-details-summary-pill">
                          <Clock className="load-history-details-summary-pill-icon" />
                          {t("summary.noFinalStatus", { defaultValue: "Sem estado final" })}
                        </span>
                      )}
                  </div>

                  {/* removido para ficar igual ao design de referência */}
                </div>
              </div>

              <div className="load-history-details-box">
                <div className="load-history-details-box-head">
                  <div className="load-history-details-box-head-left">
                    <Pencil className="load-history-details-box-head-icon" />
                    <span>{t("cards.notes")}</span>
                  </div>
                </div>

                <div className="load-history-details-notes-panel">
                  <div className="load-history-details-notes-item">
                    <div className="load-history-details-notes-item-title">{t("cards.notes")}</div>
                    <div className="load-history-details-notes-item-value">{notesText ?? "—"}</div>
                  </div>

                  <div className="load-history-details-notes-item">
                    <div className="load-history-details-notes-item-title">{t("cards.notesForDriver")}</div>
                    <div className="load-history-details-notes-item-value">{notes4driverText ?? "—"}</div>
                  </div>
                </div>
              </div>

              <div className="load-history-details-box">
                <div className="load-history-details-box-head">
                  <div className="load-history-details-box-head-left">
                    <Download className="load-history-details-box-head-icon" />
                    <span>{t("titles.docs")}</span>
                  </div>

                  <button
                    type="button"
                    className="load-history-details-btn-secondary load-history-details-docs-download"
                    disabled={!cmrPhotos?.[0]?.src}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (cmrPhotos[0]?.src) downloadCMR(cmrPhotos[0].src);
                    }}
                    title={!cmrPhotos?.[0]?.src ? t("docs.noDocs") : t("buttons.downloadCmr")}
                  >
                    <Download className="load-history-details-btn-icon" />
                    {t("buttons.downloadCmr")}
                  </button>
                </div>

                <div className="load-history-details-docs">
                  {cmrPhotos.length === 0 ? (
                    <div className="load-history-details-empty">{t("docs.noDocs")}</div>
                  ) : (
                    <div className="load-history-details-doc-groups">
                      {groupedCmrDocs.map((group) => {
                        const point = group.point;

                        const pointTypeLabel = point
                          ? getPointTypeLabel(point.type)
                          : t("docs.unknownPoint", { defaultValue: "Ponto" });

                        const pointSequence = point?.sequence ?? null;

                        const pointLocation =
                          point?.location ||
                          point?.city ||
                          point?.country ||
                          t("docs.unknownLocation", { defaultValue: "Local não identificado" });

                        return (
                          <div
                            key={group.key}
                            className={`load-history-details-doc-group ${
                              point?.type === "pickup"
                                ? "load-history-details-doc-group-pickup"
                                : point?.type === "delivery"
                                ? "load-history-details-doc-group-delivery"
                                : "load-history-details-doc-group-unknown"
                            }`}
                          >
                            <div className="load-history-details-doc-group-head">
                              <span className="load-history-details-doc-point-badge">
                                {pointSequence ? `${pointTypeLabel} #${pointSequence}` : pointTypeLabel}
                              </span>

                              <span className="load-history-details-doc-point-location">
                                <MapPin className="load-history-details-doc-point-location-icon" />
                                {pointLocation}
                              </span>
                            </div>

                            {!point && (
                              <div className="load-history-details-doc-group-warning">
                                {t("docs.missingPointMetadata", {
                                  defaultValue:
                                    "Estas imagens ainda não têm ponto associado. É necessário guardar o pickupDeliveryId no upload.",
                                })}
                              </div>
                            )}

                            <div className="load-history-details-doc-thumbs">
                              {group.docs.map((doc) => {
                                const url = toUrl(doc.src);

                                return (
                                  <button
                                    key={doc.id}
                                    type="button"
                                    className="load-history-details-doc"
                                    title={t("docs.openDocTitle", { index: doc.displayIndex })}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setPreviewSrc(url);
                                    }}
                                  >
                                    <img
                                      src={url}
                                      alt={t("docs.lightboxAlt")}
                                      className="load-history-details-doc-img"
                                      onError={(e) => {
                                        const fixed = url.replace("/uploads/uploads/", "/uploads/");
                                        if (fixed !== url) e.currentTarget.src = fixed;
                                      }}
                                    />

                                    <div className="load-history-details-doc-caption">
                                      {t("docs.docCaption", { index: doc.displayIndex })}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </main>
      </section>

      {previewSrc && (
        <div className="load-history-details-lightbox" onClick={() => setPreviewSrc(null)}>
          <div className="load-history-details-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewSrc}
              alt={t("docs.lightboxAlt")}
              className="load-history-details-lightbox-img"
              onError={(e) => {
                const fixed = previewSrc.replace("/uploads/uploads/", "/uploads/");
                if (fixed !== previewSrc) e.currentTarget.src = fixed;
              }}
            />

            <button type="button" className="load-history-details-lightbox-close" onClick={() => setPreviewSrc(null)}>
              <X className="load-history-details-close-icon" />
              {t("buttons.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, accent }) {
  return (
    <div className={`load-history-details-summary-card ${accent ? `is-${accent}` : ""}`}>
      <div className="load-history-details-summary-card-icon">
        {React.cloneElement(icon, {
          className: "load-history-details-summary-card-svg",
        })}
      </div>

      <div className="load-history-details-summary-card-content">
        <div className="load-history-details-summary-card-label">{label}</div>
        <div className="load-history-details-summary-card-value">{value ?? "—"}</div>
        {sub && <div className="load-history-details-summary-card-sub">{sub}</div>}
      </div>
    </div>
  );
}

function AssetPill({ type, icon, label, value }) {
  return (
    <div className={`load-history-details-asset-pill load-history-details-asset-pill-${type || "default"}`}>
      <div className="load-history-details-asset-pill-icon">
        {React.cloneElement(icon, {
          className: "load-history-details-asset-pill-svg",
        })}
      </div>

      <div className="load-history-details-asset-pill-content">
        <div className="load-history-details-asset-pill-label">{label}</div>
        <div className="load-history-details-asset-pill-value">{value ?? "—"}</div>
      </div>
    </div>
  );
}

function MiniField({ label, value, mono = false, className = "" }) {
  return (
    <div className={`load-history-details-mini-field ${className}`}>
      <div className="load-history-details-mini-label">{label}</div>
      <div className={`load-history-details-mini-value ${mono ? "load-history-details-mini-mono" : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}